/* eslint-disable no-nested-ternary */
import React, { ReactElement } from 'react'
import classnames from 'classnames/bind'
import { useBackgroundImageWithFallback } from '../../../hooks/useBackgroundImageWithFallback'
import * as ListingUtils from '../utils'
import SimpleMap from '../../layouts/simpleMap'
import styles from './index.scss'
import { AutomationTarget } from '../../../types'
import { parseVideoUrl } from './utils'

const cx = classnames.bind(styles)

export const LISTING_INFORMATION_PHOTO_WIDTH = 955 // was 750

export const TABS: {
  NONE: null
  PHOTOS: number
  VIDEO: number
  MAP: number
  STREET: number
} = {
  NONE: null,
  PHOTOS: 0,
  VIDEO: 1,
  MAP: 2,
  STREET: 3,
}

interface VerifiedImageProps extends AutomationTarget {
  url: string
  featureFlags: Record<string, boolean>
  defaultPhotoConfigKey?: string
  className?: string
  alt?: string
  onClick?: () => void
}

export const VerifiedImage = ({
  url,
  featureFlags,
  defaultPhotoConfigKey,
  ...restProps
}: VerifiedImageProps) => {
  const { formatListingImgUrl } = ListingUtils.ListingFormatters

  // For <img> tags, we don't need the verification hook since the browser
  // handles image loading natively. Just use the URL directly.
  // Pass alreadyVerifiedPhoto to bypass the hook's verification logic
  const mainImage = useBackgroundImageWithFallback(
    url,
    featureFlags['fe.resi.enabled-photo-enhancement'],
    url, // Pass url as alreadyVerifiedPhoto to skip verification
    undefined,
    defaultPhotoConfigKey
  ) as string

  const finalSrc = formatListingImgUrl(`${mainImage}`, {
    width: LISTING_INFORMATION_PHOTO_WIDTH,
  }) as string

  return <img src={finalSrc} {...restProps} />
}

interface BackgroundImageProps {
  url?: string
  featureFlags: Record<string, boolean>
  className?: string
  onClick?: () => void
  defaultPhotoConfigKey?: string
}

export const BackgroundImage = ({
  url,
  featureFlags,
  className,
  defaultPhotoConfigKey,
  ...restProps
}: BackgroundImageProps) => {
  const mainImage = useBackgroundImageWithFallback(
    url,
    featureFlags['fe.resi.enabled-photo-enhancement'],
    undefined,
    undefined,
    defaultPhotoConfigKey
  ) as string
  return (
    <div
      className={cx('background-image', className)}
      style={{ backgroundImage: `url(${mainImage})` }}
      {...restProps}
    />
  )
}

interface SecondaryItemProps {
  index: number
  renderer: () => JSX.Element
  className?: string
}

export const SecondaryItem = ({
  index,
  renderer,
  className,
}: SecondaryItemProps) => (
  <div
    key={index}
    className={cx('secondary-photo', `index-${index}`, 'priority', className)}
  >
    {renderer()}
  </div>
)

interface MapItemProps {
  defaultView?: ReactElement
  index: number
  coords: { lat: number; lng: number } | undefined
  setActiveTab: (tab: number) => void
  showStreetView?: boolean
  mapTabIndex: number
  streetTabIndex: number
}

export const MapItem = ({
  defaultView,
  index,
  coords,
  setActiveTab,
  showStreetView = false,
  mapTabIndex,
  streetTabIndex,
}: MapItemProps) => (
  <SecondaryItem
    index={index}
    renderer={() => (
      <SimpleMap
        defaultView={defaultView}
        coords={coords}
        createMapOptions={() => ({
          mapTypeControl: false,
        })}
        onMapClick={() => {
          setActiveTab(showStreetView ? streetTabIndex : mapTabIndex)
        }}
        showStreetView={showStreetView}
      />
    )}
  />
)

interface VideoItemProps {
  index: number
  setActiveTab: (tab: number) => void
  videoUrl?: string
  videoTabIndex: number
}

export const VideoItem = ({
  index,
  setActiveTab,
  videoUrl,
  videoTabIndex,
}: VideoItemProps) => {
  const parsed = videoUrl ? parseVideoUrl(videoUrl) : null

  return (
    <SecondaryItem
      index={index}
      renderer={() => (
        <div
          onClick={() => setActiveTab(videoTabIndex)}
          className={cx('video-item-container')}
        >
          {parsed?.type === 'embed' ? (
            <iframe
              src={parsed.url}
              title='Property Video'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
              allowFullScreen
              className={cx('video-item-iframe')}
            />
          ) : parsed?.type === 'direct' ? (
            <video src={parsed.url} muted className={cx('video-item-iframe')} />
          ) : (
            <>
              <div className={cx('video-item-play-button')}>▶</div>
              <div className={cx('video-item-label')}>Video</div>
            </>
          )}
        </div>
      )}
    />
  )
}
